// ============================================================
// TERROR TCG — COMPANION SPRITE (component only)
//
// REGISTRY MERGE (July 3, 2026): this file used to RE-DECLARE the
// entire companion registry (COMPANIONS, TIERS, helpers, radiant
// tables) in parallel with companions.js — two sources of truth
// that had already drifted (divergent RADIANT_TIER_COST prices and
// a different RADIANT_VERSIONS catalog; the live UI + progression
// engine read companions.js, so those were the canon values).
//
// Now: companions.js is the ONE registry. This file keeps only the
// sprite component, and re-exports the registry names for
// back-compat so existing imports from "./CompanionSprite" (e.g.
// CompanionInteraction) keep working — they simply receive the
// canonical values.
//
// The 44 per-companion originLore lines that previously lived only
// here were ported INTO companions.js in the same deploy.
// ============================================================

import { useState, useEffect, useRef } from "react";
import { getCompanionSprite, getCompanionFallback } from "./SpriteManifest";
// SHIP 10 — the body. A caller that opts in with `body` gets the kit's
// 3D being in this same square when the companion HAS one; the webp
// stays for the ids that don't (Esclings, trophies, J'ynxi) and for
// every grid that never opts in. See companionBody3D.jsx.
import CompanionBody3D, { companionBodyOf } from "./companionBody3D";
// KING'S GRACE (2026-09-16) — the worn foil is drawn by the shared kit,
// not by a blurred colour disc. `radiantTier` keeps its name (every
// caller already passes it) but now carries a Grace id.
import { GraceAura2D } from "./GraceAura";
import { getGrace } from "./kingsGrace";

// ── Canonical registry re-exports (single source: companions.js) ──
export {
  TIERS,
  SOL_CONVERTER_COSTS,
  ILK_COLORS,
  VARIANTS,
  COMPANIONS,
  getCompanion,
  getSolConverterCost,
  getStarterCompanionId,
  isCompanionUnlocked,
  getUnlockedCompanions,
  getUnlockHint,
  getCompanionProvenance,
  groupCompanionsByTier,
  RADIANT_TIER_COST,
  RADIANT_VERSIONS,
} from "./companions";

// Radiant aura pulse keyframe — injected once, lifecycle-proof.
if (typeof document !== "undefined" && !document.getElementById("comp-radiant-kf")) {
  const el = document.createElement("style");
  el.id = "comp-radiant-kf";
  el.textContent =
    "@keyframes comp-radiant-pulse{0%,100%{opacity:0.7;transform:scale(0.98);}50%{opacity:1;transform:scale(1.04);}}";
  document.head.appendChild(el);
}

// ─────────────────────────────────────────────────────────────
// COMPANION SPRITE COMPONENT
// Renders the companion's real CDN art (the SAME source every other
// room uses via PlayerEntity), so the companion looks identical in the
// Plaza, the Affinity hall, dungeons, the workshop — everywhere.
//
//   companionId → `${CDN}/${id}.webp`  (own art)
//   on 404      → J'ynxi prototype art (graceful, never blank)
//   radiantTier → optional holo sheen ring behind the sprite
//
// This replaced the old procedural-orb placeholder, which is exactly
// why the Plaza companion looked different from the one in the Affinity
// room — Plaza drew a colored circle while the halls drew real art.
// One art source now, no drift.
// ─────────────────────────────────────────────────────────────

function CompanionSpriteComponent({ companionId, size = 120, variant = "standard", radiantTier = null, body = false, facing = null, mood = "idle", shot = null, still = false }) {
  const own = companionId ? getCompanionSprite(companionId) : null;
  const [fellBack, setFellBack] = useState(false);
  const prevOwn = useRef(own);
  useEffect(() => {
    if (prevOwn.current !== own) { prevOwn.current = own; setFellBack(false); }
  }, [own]);

  if (!companionId) return null;

  const src = fellBack ? getCompanionFallback() : own;
  // A retired or unknown Grace resolves to null and draws NOTHING (Doctrine §5).
  const aura = radiantTier ? (getGrace(radiantTier)?.color || null) : null;
  // A body draws its Grace in 3D inside its own canvas; only a flat
  // picture gets the DOM layers. Never both.
  const asBody = !!(body && companionBodyOf(companionId));
  const flatAura = aura && !asBody;

  return (
    <div style={{
      position: "relative", width: size, height: size,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {flatAura && <GraceAura2D graceId={radiantTier} layer="back" still={still} />}
      {asBody ? (
        <CompanionBody3D companionId={companionId} size={size} facing={facing || { x: 0, y: 1 }} mood={mood} shot={shot} grace={aura ? radiantTier : null}
          style={{ position: "relative", zIndex: 1, filter: aura ? `drop-shadow(0 0 8px ${aura}aa)` : "drop-shadow(0 0 6px #c9a84c44)" }} />
      ) : (
      <img
        key={src}
        src={src}
        alt="companion"
        onError={() => { if (!fellBack) setFellBack(true); }}
        style={{
          width: "100%", height: "100%", objectFit: "contain",
          imageRendering: "pixelated",
          filter: aura ? `drop-shadow(0 0 8px ${aura}aa)` : "drop-shadow(0 0 6px #c9a84c44)",
          position: "relative", zIndex: 1,
        }}
      />
      )}
      {/* front layer must out-rank the sprite's zIndex 1 */}
      {flatAura && <GraceAura2D graceId={radiantTier} layer="front" still={still} style={{ zIndex: 2 }} />}
    </div>
  );
}

export default CompanionSpriteComponent;
